'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@ahafood/ui/components/drawer';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@ahafood/ui/components/sheet';
import { cn } from '@ahm/common-helpers';
import { Settings2Icon } from 'lucide-react';
import type React from 'react';
import { useNavigation } from '@/components/navigation/navigation-context';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useIsMobile } from '@/lib/is-mobile';

interface FilterDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  tempFilters: {
    category_slug: string;
    promotion: boolean;
    freeship: boolean | string;
    special_request: string;
    nearby: boolean;
    open_now: boolean;
  };
  setTempFilters: React.Dispatch<
    React.SetStateAction<{
      category_slug: string;
      promotion: boolean;
      freeship: boolean | string;
      special_request: string;
      nearby: boolean;
      open_now: boolean;
    }>
  >;
  categories: {
    id: number;
    slug: string;
    title_vi_vn: string;
  }[];
  textValue: string;
  activeCount: number;
  onApply: () => void;
  onReset: () => void;
}

function FilterContent({
  tempFilters,
  setTempFilters,
  categories,
  textValue,
  onApply,
  onReset,
}: Omit<FilterDrawerProps, 'isOpen' | 'setIsOpen' | 'activeCount'>) {
  return (
    <>
      <ScrollArea className="px-4 flex-1 overflow-y-auto">
        {/* --- Special Options --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Tùy chọn quán</h3>
            <p className="text-sm text-neutral-60 mb-4">Có thể chọn nhiều</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.nearby && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, nearby: !p.nearby }))
              }
            >
              Gần tôi (dưới 5km)
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.promotion && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, promotion: !p.promotion }))
              }
            >
              Ưu đãi
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                tempFilters.open_now && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((p) => ({ ...p, open_now: !p.open_now }))
              }
            >
              Mở cửa
            </Button>
          </div>
        </div>

        {/* --- Delivery Fee --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Phí giao hàng</h3>
            <p className="text-sm text-neutral-60 mb-4">Chỉ chọn một</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className={cn(
                'flex flex-col gap-2 p-3 rounded-lg border transition-all',
                tempFilters.freeship === 'freeship' && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((prev) => ({
                  ...prev,
                  freeship: prev.freeship === 'freeship' ? false : 'freeship',
                }))
              }
            >
              <span className="text-sm font-medium">Freeship</span>
            </Button>

            <Button
              variant="outline"
              className={cn(
                'flex flex-col gap-2 p-3 rounded-lg border transition-all',
                tempFilters.freeship === 'freeship-100' && 'border-primary-50'
              )}
              onClick={() =>
                setTempFilters((prev) => ({
                  ...prev,
                  freeship:
                    prev.freeship === 'freeship-100' ? false : 'freeship-100',
                }))
              }
            >
              <span className="text-sm font-medium">Freeship 100%</span>
            </Button>
          </div>
        </div>

        {/* --- Categories --- */}
        <div className="mb-6">
          <div className="flex justify-between">
            <h3 className="text-base font-semibold mb-3">Danh mục</h3>
            <p className="text-sm text-neutral-60 mb-4">Chỉ chọn một</p>
          </div>
          {textValue && (
            <div className="mb-3 p-2 bg-yellow-10 rounded-lg">
              <p className="text-xs text-yellow-60">
                Không thể chọn danh mục khi đang tìm kiếm bằng từ khóa
              </p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant="outline"
                disabled={!!textValue}
                onClick={() =>
                  setTempFilters((p) => ({
                    ...p,
                    category_slug:
                      p.category_slug === category.slug ? '' : category.slug,
                  }))
                }
                className={cn(
                  'flex flex-col h-auto p-3',
                  tempFilters.category_slug === category.slug &&
                    'border-primary-50',
                  textValue && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="text-xs text-center">
                  {category.title_vi_vn}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </ScrollArea>

      <div className="px-4 pt-4">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onReset}
            className="flex-1 h-12 border-neutral-20 text-neutral-70 hover:bg-neutral-10 bg-transparent"
          >
            Đặt lại
          </Button>
          <Button
            onClick={onApply}
            className="flex-1 h-12 bg-primary-50 hover:bg-primary-60 text-white"
          >
            Áp dụng
          </Button>
        </div>
      </div>
    </>
  );
}

export function FilterDrawer({
  isOpen,
  setIsOpen,
  tempFilters,
  setTempFilters,
  categories,
  textValue,
  activeCount,
  onApply,
  onReset,
}: FilterDrawerProps) {
  const { bottomPosition } = useBottomPosition();
  const { isNavigationVisible } = useNavigation();
  const isMobile = useIsMobile();

  const TriggerButton = (
    <Button
      style={{
        backgroundColor: activeCount <= 0 ? '#c9c9c9' : undefined,
      }}
      className={cn(
        'rounded-xl px-4 gap-2 font-medium transition-colors relative flex items-center [&>svg]:size-5',
        activeCount > 0 && 'bg-primary'
      )}
    >
      <Settings2Icon />
      Bộ lọc
      {activeCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-primary-10 text-primary-50 px-1.5 py-0.5 rounded-full text-xs font-bold min-w-[20px] h-5 flex items-center justify-center">
          {activeCount}
        </span>
      )}
    </Button>
  );

  if (isMobile) {
    // Mobile: Use Drawer (bottom sheet)
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>{TriggerButton}</DrawerTrigger>
        <DrawerContent
          className="max-h-[90dvh] flex flex-col"
          style={{ bottom: isNavigationVisible ? bottomPosition : 0 }}
        >
          <DrawerHeader className="pb-2">
            <p className="text-sm text-neutral-60 text-left">Lọc theo</p>
          </DrawerHeader>
          <DrawerTitle className="hidden" />

          <FilterContent
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            categories={categories}
            textValue={textValue}
            onApply={onApply}
            onReset={onReset}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use Sheet (left side panel)
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{TriggerButton}</SheetTrigger>
      <SheetContent
        side="left"
        className="w-[400px] sm:w-[400px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Bộ lọc</SheetTitle>
          <p className="text-sm text-neutral-60 text-left">Lọc theo</p>
        </SheetHeader>

        <FilterContent
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          categories={categories}
          textValue={textValue}
          onApply={onApply}
          onReset={onReset}
        />
      </SheetContent>
    </Sheet>
  );
}
